<template>
	<ActionsMenu
		v-bind="$attrs"
		v-on="$listeners"
		:items="items"
		:selectedApp="selectedApp"
		:loading="loadingActions || isNodeDataLoading"
		:editNode="editNode.id"
	/>
</template>

<script>
import ActionsMenu from './ActionsMenu.vue';
import ActionsMenuService from './actions-menu-service';
import { getAction } from '@/store/modules/actions/getters';
import { getActionsOfServiceActionCollection } from '@/store/modules/apps/actions';
import {
	getActiveFetchOfActionsForServiceVersion,
	getService,
	getServiceActionCollection,
	getServiceActionCollectionActions,
	getServiceActionsByVersion,
} from '@/store/modules/apps/getters';
import { setIsChangingAction } from '@/store/modules/blockEditor/actions';
import { editNode, isNodeDataLoading } from '@/store/modules/selectedNode/getters';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import AppActionService from '@/utils/AppActionService';

// Helper

const isActionTrigger = (action) => action.is_trigger;

export default {
	name: 'ActionsMenuForEditNodeDataProvider',
	components: { ActionsMenu },
	props: { selectedServiceActionCollectionId: { type: Number } },
	computed: {
		...mapGettersShorthand({
			editNode,
			getAction,
			getService,
			getServiceActionsByVersion,
			getActiveFetchOfActionsForServiceVersion,
			getServiceActionCollection,
			getServiceActionCollectionActions,
			editWorkflow,
			isNodeDataLoading,
		}),
		allActions() {
			if (!this.editNode) {
				return [];
			}
			const actions = this.selectedAppIsServiceActionCollection
				? this.getServiceActionCollectionActions(this.selectedApp.id).map((action) =>
						ActionsMenuService.populateActionWithIntegrationDetails(action, this.selectedApp)
				  )
				: this.getServiceActionsByVersion(this.editNode.service, this.editNode.service_version) || [];
			const sortByName = (a, b) => {
				const aName = a.title.toUpperCase();
				const bName = b.title.toUpperCase();
				return aName < bName ? -1 : aName > bName ? 1 : 0;
			};
			return actions.filter((action) => !action.hidden).sort(sortByName);
		},
		actions() {
			return this.allActions.filter((action) => !isActionTrigger(action));
		},
		triggers() {
			return this.allActions.filter((action) => isActionTrigger(action));
		},
		editingTrigger() {
			return this.editWorkflow?.trigger_config?.node === this.editNode.id;
		},
		items() {
			// Logic for triggers?
			if (this.editingTrigger) {
				return this.triggers;
			}
			return this.actions;
		},
		selectedAction() {
			return this.getAction(this.editNode.action);
		},
		selectedApp() {
			if (this.selectedServiceActionCollectionId || AppActionService.doesActionHasServiceActionCollection(this.selectedAction)) {
				const serviceActionCollectionId =
					this.selectedServiceActionCollectionId || AppActionService.getActionServiceActionCollection(this.selectedAction);
				return this.getServiceActionCollection(serviceActionCollectionId);
			}
			return this.getService(this.editNode.service);
		},
		selectedAppIsServiceActionCollection() {
			return this.selectedApp?.isServiceActionCollection;
		},
		loadingActions() {
			const fetchKey = this.selectedAppIsServiceActionCollection
				? AppActionService.buildServiceActionCollectionActionsFetchKey(this.selectedApp.id)
				: AppActionService.buildActionsFetchKey(this.editNode?.service, this.editNode?.service_version);
			return !!this.getActiveFetchOfActionsForServiceVersion(fetchKey);
		},
	},
	methods: { ...mapActionsShorthand({ getActionsOfServiceActionCollection, setIsChangingAction }) },
	watch: {
		selectedAppIsServiceActionCollection: {
			handler(newV) {
				newV && this.getActionsOfServiceActionCollection({ id: this.selectedApp.id });
			},
			immediate: true,
		},
	},
	beforeDestroy() {
		this.setIsChangingAction(false);
	},
};
</script>

<style lang="scss" scoped></style>
